/**
 * HTTP API — Spring 백엔드(trinitylaser-api) 호출.
 *
 * 응답 봉투 ApiResponse { success, message, data } 를 벗겨 data 만 돌려준다.
 * 실패는 ApiError 로 통일한다.
 */
import axios from 'axios';
import { API_BASE_URL } from '../config/env';
import { ApiError } from './errors';

let tokenGetter = () => null;
let onUnauthorized = () => {};

export function configureHttp({ getToken, unauthorized }) {
  if (getToken) tokenGetter = getToken;
  if (unauthorized) onUnauthorized = unauthorized;
}

const http = axios.create({
  baseURL: API_BASE_URL,
  timeout: 20000,
  headers: { 'Content-Type': 'application/json' }
});

http.interceptors.request.use(config => {
  const token = tokenGetter();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

http.interceptors.response.use(
  res => {
    const body = res.data;
    if (body && typeof body === 'object' && 'success' in body) {
      if (body.success === false) {
        throw new ApiError(body.message || 'Request failed.', { status: res.status, code: 'API' });
      }
      return body.data;
    }
    return body;
  },
  err => {
    if (err instanceof ApiError) return Promise.reject(err);
    const status = err.response?.status || 0;
    const message =
      err.response?.data?.message ||
      (status === 0 ? 'Cannot reach the server. Check your connection and try again.' : err.message) ||
      'Request failed.';
    if (status === 401) onUnauthorized();
    return Promise.reject(new ApiError(message, { status, code: status === 0 ? 'NETWORK' : 'HTTP', details: err.response?.data }));
  }
);

/** 페이지 응답 정규화 — {items,total,page,size} 형태로 */
function page(res, fallbackPage = 1, fallbackSize = 10) {
  if (Array.isArray(res)) return { items: res, total: res.length, page: 1, size: res.length, totalPages: 1 };
  const items = res.items || res.content || [];
  const total = res.total ?? res.totalElements ?? items.length;
  const size = res.size ?? fallbackSize;
  // Spring 스타일(content/totalElements)은 0-based page, 목 스타일(items)은 1-based
  const zeroBased = res.content !== undefined || res.number !== undefined;
  const rawPage = res.page ?? res.number ?? (zeroBased ? fallbackPage - 1 : fallbackPage);
  return {
    items,
    total,
    page: zeroBased ? rawPage + 1 : rawPage,
    size,
    totalPages: res.totalPages ?? Math.max(1, Math.ceil(total / size))
  };
}

const auth = {
  login: body => http.post('/auth/login', body),
  signup: body => http.post('/auth/signup', body),
  me: () => http.get('/users/me'),
  updateProfile: body => http.put('/users/me', body),
  changePassword: body => http.put('/users/me/password', body),
  requestPasswordReset: body => http.post('/auth/password-reset', body),
  logout: () => Promise.resolve({ ok: true })
};

const orders = {
  list: async ({ status, q, from, to, page: p = 1, size = 10 } = {}) =>
    page(await http.get('/orders', { params: { status: status && status !== 'ALL' ? status : undefined, q, from, to, page: p - 1, size } }), p, size),
  get: orderNumber => http.get(`/orders/${encodeURIComponent(orderNumber)}`),
  summary: () => http.get('/orders/summary')
};

const estimations = {
  list: () => http.get('/estimations'),
  get: quoteNumber => http.get(`/estimations/${encodeURIComponent(quoteNumber)}`),
  create: body => http.post('/estimations', body),
  accept: quoteNumber => http.post(`/estimations/${encodeURIComponent(quoteNumber)}/accept`)
};

const notifications = {
  list: () => http.get('/notifications'),
  unreadCount: async () => {
    const r = await http.get('/notifications/unread-count');
    return typeof r === 'number' ? r : r?.count ?? 0;
  },
  markRead: id => http.put(`/notifications/${id}/read`),
  markAllRead: () => http.put('/notifications/read-all')
};

const materials = {
  list: (params = {}) => http.get('/materials', { params })
};

const certificates = {
  list: () => http.get('/certificates'),
  byDocket: docket => http.get('/certificates/search', { params: { docket } })
};

const nesting = {
  orders: () => http.get('/nesting/orders'),
  byOrder: orderNumber => http.get(`/nesting/${encodeURIComponent(orderNumber)}`)
};

const inquiries = {
  create: body => {
    const { attachment, ...rest } = body;
    if (attachment) {
      const fd = new FormData();
      Object.entries(rest).forEach(([k, v]) => fd.append(k, v ?? ''));
      fd.append('attachment', attachment);
      return http.post('/inquiries', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    }
    return http.post('/inquiries', rest);
  }
};

const gallery = {
  list: () => http.get('/gallery')
};

const httpApi = { auth, orders, estimations, notifications, materials, certificates, nesting, inquiries, gallery };
export default httpApi;
